import { Input } from '@repo/ui/components/input';
import { cn } from '@repo/ui/lib/utils';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

/**
 * A password field with a show/hide toggle, for the back office.
 *
 * Every prop lands on the `Input`, not on the wrapper, so this drops into
 * `FormControl render={…}` where a bare `Input` used to go: the generated `id`,
 * `aria-describedby` and `aria-invalid` reach the field itself and the label
 * keeps pointing at it.
 *
 * The button is `type="button"`. A bare `<button>` inside a form submits it,
 * which on the sign-in screen would send a half-typed password to the API and
 * spend one of the attempts before the lockout.
 *
 * Not for the till: a PIN on a terminal the public stands in front of has
 * nowhere private to be shown, and `StartShift` says so in its own comment.
 */
function PasswordInput({
  className,
  // The toggle owns the type. Taking one from a caller and spreading it below
  // would let `type="password"` pin the field shut while the button says it is
  // showing.
  type: _type,
  ...props
}: React.ComponentProps<'input'>) {
  const [shown, setShown] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  // A form that resets — ChangeCredential does, after it succeeds — leaves a
  // shown password on screen otherwise, because the value clears but this does
  // not. The listener is on the owning form, which is where the event fires.
  useEffect(() => {
    const form = field.current?.form;

    if (!form) {
      return;
    }

    const hide = () => setShown(false);

    form.addEventListener('reset', hide);

    return () => form.removeEventListener('reset', hide);
  }, []);

  return (
    <div className="relative">
      <Input
        ref={field}
        type={shown ? 'text' : 'password'}
        // Room for the button, so a long value scrolls under the label rather
        // than behind the icon.
        className={cn('pr-12', className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setShown((was) => !was)}
        // The name says what the control does next, not what the field is
        // doing now — a screen reader user gets "Show password" while it is
        // hidden, which is the action available to them.
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-3xl text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30 focus-visible:outline-none"
      >
        {shown ? (
          <IconEyeOff className="size-5" aria-hidden="true" />
        ) : (
          <IconEye className="size-5" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

export { PasswordInput };
